// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState } from 'react';

import { type MachineElement, useEvolveDelete } from '@evolvemep/api-ts';
import { Button, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { hasLengthExactly } from 'helpers/helper-types';

type Props = {
  selectedElements: MachineElement[];
  onPartsDeleted: () => void;
};

export const DeletePartsModal = ({ selectedElements, onPartsDeleted }: Props) => {
  const [opened, setOpened] = useState(false);
  const { apiCall: deletePartsApiCall } = useEvolveDelete('shop/fabrication/element');

  return (
    <>
      <Button
        variant="light"
        disabled={selectedElements.length === 0}
        onClick={() => setOpened(true)}
        leftSection={<EvolveIcon icon="Delete" />}
      >
        Delete
      </Button>

      <WrappedModal
        title={`Delete part${selectedElements.length === 1 ? '' : 's'}`}
        opened={opened}
        onClose={() => setOpened(false)}
        onConfirm={async () => {
          await deletePartsApiCall({
            params: {
              elementIds: selectedElements.map((e) => e.elementId),
            },
          });
          onPartsDeleted();
        }}
        confirmationText="Delete"
      >
        <>
          <Text>
            Are you sure you want to delete {selectedElements.length === 1 ? 'this' : 'these'}{' '}
            <b>
              {hasLengthExactly(selectedElements, 1) ? selectedElements[0].name : `${selectedElements.length} parts`}
            </b>
            ?
          </Text>
          <Text>
            In order to repopulate {selectedElements.length === 1 ? 'this part, it' : 'these parts, they'} would need to
            be resent from Revit.
          </Text>
        </>
      </WrappedModal>
    </>
  );
};
